import * as React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { Autocomplete, Button, IconButton, FormControl, Typography, TextField } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { GridRowsProp, GridRowModesModel, GridRowModes, GridActionsCellItem, GridEventListener } from '@mui/x-data-grid';
import { GridRowId, GridRowModel, GridRowEditStopReasons } from '@mui/x-data-grid';
import { copyArrayWithIndexChanged } from '../Utils';

import { Page } from '../subs/Page';
import { Link, useParams } from 'react-router-dom';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../contexts/SnackBarContext';
import { UserContext } from "../UserProvider";
import { httpGet } from '../Web';
import { SuccessFailureIndicator } from '../subs/SuccessFailureIndicator';
import { PageSection } from '../subs/PageSection';
import { WorkshopUser, updateWorkshopUser, deleteWorkshopUser, WorkshopUserStatus, WorkshopUserStatusStrings } from '../types/WorkshopUser';
import { User, UserStatus, isGlobalAdmin, getAllUsers } from '../types/User';

declare module '@mui/x-data-grid' {
    interface ToolbarPropsOverrides {
        setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
        setRowModesModel: (
            newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
        ) => void;
    }
}

export interface SimpleDialogProps {
    open: boolean;
    onClose: (value: User | undefined) => void;
}

function compareFn(a: User, b: User) {
    return a.name.localeCompare(b.name);
}

function AddUserDialog(props: SimpleDialogProps) {
    const { onClose, open } = props;
    const [allUsers, setAllUsers] = React.useState<User[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<User>();
    const { showSnackbar } = useSnackbar();

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllUsers = async () => {
            const allUsersResponse = await getAllUsers(new AbortController());
            if (allUsersResponse.failed) {
                showUserFeedback(allUsersResponse.errorText);
                return;
            }
            let allUsersLoc: User[] = allUsersResponse.result;
            allUsersLoc = allUsersLoc.filter(user => user.status !== UserStatus.Disabled); // filter out users that are not active
            allUsersLoc.sort(compareFn);
            setAllUsers(allUsersLoc);
        }
        fetchAllUsers();
    }, [showUserFeedback]);

    const handleClose = () => {
        onClose(undefined);
    };

    const handleListItemClick = () => {
        onClose(selectedUser);
        setSelectedUser(undefined);
    };

    return <Dialog onClose={handleClose} open={open} fullWidth>
        <DialogTitle>{"Please enter the users email"}</DialogTitle>
        <DialogContent>
            <FormControl fullWidth >
                <Autocomplete fullWidth
                    id='UserEmail'
                    options={allUsers}
                    value={selectedUser}
                    //disabled={false}
                    renderInput={(params) => <TextField multiline {...params} />}
                    renderOption={(props, user) => {
                        return (
                            <li {...props} key={user.id}>
                                {user.name + ' - ' + user.email}
                            </li>
                        );
                    }}
                    getOptionLabel={(user) => {
                        // Regular option
                        return user.email;
                    }}
                    onChange={(event, newValue) => {
                        setSelectedUser(newValue ?? undefined);
                    }}
            />
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button
                style={{
                    marginRight: "auto"
                }}
                aria-label="close"
                onClick={handleClose}
                color="primary"
            >
                <CancelIcon />
            </Button>
            <Button
                aria-label="add user"
                onClick={() => {
                    if (selectedUser) {
                        handleListItemClick();
                    } else {
                        showUserFeedback("Please select a user", "error");
                    }
                }}
                color="primary"
            >
                <AddIcon />
            </Button>
        </DialogActions>
    </Dialog>
}

export const WSUserAdministration = () => {
    const { wsId: workshopID } = useParams();
    const user = React.useContext(UserContext);
    const [wsUsersData, setWsUsers] = React.useState<WorkshopUser[]>([]);
    const [open, setOpen] = React.useState(false);
    const [globalAdmin, setIsGlobalAdmin] = React.useState(false);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const { showSnackbar } = useSnackbar();

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        fetch();

        async function fetch() {
            if (workshopID === undefined) return;
            let query = `api/workshop/user/${workshopID}`;
            console.log('query: ', query, " length: ", query.length);

            let isGlobalAdminResponse = await isGlobalAdmin(user.user.id!!, new AbortController());

            setIsGlobalAdmin(isGlobalAdminResponse);

            let response = await httpGet(query, new AbortController());
            console.log(response);
            if (response?.errorCode) {
                setWsUsers([]);
                showUserFeedback(response);
            }
            else {
                let wsUsers: WorkshopUser[] = response?.result;
                setWsUsers(wsUsers);
            }
        }
    }, [workshopID, showUserFeedback, user.user.id])

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id: GridRowId) => () => {
        const wsUser = wsUsersData.find((row) => row.id === id);
        if (wsUser) deleteHandler(wsUser);
    };

    const AddUserButton = () => {
        setOpen(true);
    }

    const handleClose = (value: User | undefined) => {
        setOpen(false);
        if (value === undefined || workshopID === undefined || value.id === undefined) {
            return;
        }
        const newUser: WorkshopUser = {
            isWorkshopAdmin: false,
            status: WorkshopUserStatus.Registered,
            workshopId: Number(workshopID),
            userId: value.id
        };
        saveHandler(newUser);
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });

        const editedRow = wsUsersData.find((row) => row.id === id);
        if (editedRow!.id === 0) {
            setWsUsers(wsUsersData.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = wsUsersData.find(row => row.id === newRow.id);
        if (!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.status = newRow.status as number;
        changedData.isWorkshopAdmin = newRow.isWorkshopAdmin as boolean;
        saveHandler(changedData);
        setWsUsers(wsUsersData.map((row) => (row.id === newRow.id ? newRow as WorkshopUser : row)));
        return newRow;
    };

    const deleteHandler = async (changedData: WorkshopUser) => {
        if (changedData.id === undefined) {
            return;
        }
        const updateUserRequest = await deleteWorkshopUser(changedData.id, new AbortController());

        if (updateUserRequest.failed) {
            showUserFeedback(updateUserRequest.errorText);
            return;
        }
        const updateUser = updateUserRequest.result;
        let wsUsersDataLoc = wsUsersData.filter(tc => tc.id !== updateUser.id);
        setWsUsers(wsUsersDataLoc);
    }

    const saveHandler = async (changedData: WorkshopUser) => {
        const updateUserRequest = await updateWorkshopUser(changedData, new AbortController());
      
        if (updateUserRequest.failed) {
            showUserFeedback(updateUserRequest.errorText);
            return;
        }
        const updateUser = updateUserRequest.result;
        let wsUsersDataLoc = copyArrayWithIndexChanged(wsUsersData, wsUsersData.findIndex(tc => tc.id === updateUser.id), updateUser);
        setWsUsers(wsUsersDataLoc);
    }

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const cols: GridColDef[] = [
        {
            field: 'companyName',
            headerName: "Company",
            flex: 3,
            renderCell: (params) => <Typography variant='bold'>{params.row.companyName}</Typography>
        },
        {
            field: 'FirstName',
            headerName: "First name",
            flex: 2,
            valueGetter: (value, row) => {
                return row.name.slice(0, row.name.indexOf(" "));
            },
        },
        {
            field: 'LastName',
            headerName: "Last name",
            flex: 2,
            valueGetter: (value, row) => {
                return row.name.slice(row.name.indexOf(" "), row.name.length);
            },
        },
        {
            field: 'email',
            headerName: "Email",
            flex: 4,
            renderCell: (params) => <Link to={`mailto:${params.row.email}`}>{params.row.email}</Link>
        },
        {
            field: 'Registered',
            headerName: "Registered",
            flex: 1,
            type: 'boolean',
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.status >= 1} /> },
            sortable: false,
        },
        {
            field: 'Confirmed',
            headerName: "Confirmed",
            flex: 1,
            type: 'boolean',
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.status === 2} /> },
            sortable: false,
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 2,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return WorkshopUserStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return WorkshopUserStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let status = 0;
                if (WorkshopUserStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < WorkshopUserStatusStrings.length; i++) {
                        if (WorkshopUserStatusStrings[i] === value) {
                            status = i;
                            break;
                        }
                    }
                }
                return { ...row, status: status };
            },
            type: 'singleSelect',
            valueOptions: WorkshopUserStatusStrings
        },
        {
            field: 'isWorkshopAdmin',
            headerName: 'WS Admin',
            type: 'boolean',
            flex: 1,
            renderCell: (params) => {
                return <SuccessFailureIndicator success={params.row.isWorkshopAdmin} />
            },
            editable: globalAdmin,
        },
        {
            field: 'id'
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        disabled={!globalAdmin}
                        label="Delete"
                        className="textPrimary"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ];
            },
        }
    ]

    return <Page heading={"The following users have registered for this workshop."} backButton breadcrumbs>{[
        <PageSection>
            {[<div style={{ display: 'flex', flexDirection: 'column' }}>
                <AddUserDialog
                    open={open}
                    onClose={handleClose}
                />
                <Grid container rowSpacing={20} >
                    <Grid size={11}>
                    </Grid>
                    <Grid size={1} style={{ display: "flex" }}>
                        <Typography mt="auto" mb="auto">Add User:</Typography>
                        <IconButton aria-label="add user" onClick={AddUserButton} style={{ marginLeft: "auto" }} disabled={!globalAdmin} >
                            <AddIcon />
                        </IconButton>
                    </Grid>
                </Grid>
                <DataGrid
                    rows={wsUsersData}
                    columns={cols}
                    hideFooter
                    disableColumnMenu
                    showCellVerticalBorder
                    editMode="row"
                    getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                    columnVisibilityModel={{ id: false }}
                />
            </div>]}
        </PageSection>
    ]}
    </Page>
}
